import React, { useState } from "react";
import { X, CheckCircle2, Paperclip, Plus, FileText } from "lucide-react";
import { httpClient } from "../../../../../HttpClient";
import { dialog } from "../../../../../components/ui/Dialog";
import { showStatusToast } from "../../../../../components/ui/Toast";
import { useEtatGlobal } from "../../../../EtatGlobal.js";
import UserSearchTagPicker from "../../../../../components/ui/UserSearchTagPicker";
import AvatarInitials from "../atoms/AvatarInitials";
import { uploadFileToBlob } from "../../../../../utils/uploadFileToBlob";

const QUALITE_ORGANISATION_OPTIONS = [
  "Très bien organisé",
  "Correctement organisé",
  "Partiellement organisé",
  "Mal organisé",
];

const STAGE_META = {
  1: {
    title: "1 / Validation SCAN / TRI",
    label: "Numérisation & Dépôt",
    submitLabel: "Soumettre",
  },
  2: {
    title: "2 / Traitement comptable niv.2",
    label: "Pré-compta",
    submitLabel: "Soumettre",
  },
  3: {
    title: "Formulaire de clôture",
    label: "Révision",
    submitLabel: "Valider la clôture",
  },
};

// Mission Suivi Compta auto-validée par chaque étape (voir comptaAutoLink.js
// côté backend) — pour le toast de confirmation après clôture.
const STAGE_TO_COMPTA_MISSION = {
  1: "Tri et numérisation",
  2: "Traitement niv.2",
  3: "Traitement niv.1",
};

// Fichiers joints à la clôture (étapes 2 et 3) : 5 au plus, ajoutés un par un.
// Envoyés sur Vercel Blob à la validation, puis joints au mail de clôture.
const FICHIERS_MAX = 5;
const EXTENSIONS_FICHIERS = ".pdf,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.heic";
const tailleLisible = (o) => (o >= 1024 * 1024 ? `${(o / 1024 / 1024).toFixed(1).replace(".", ",")} Mo` : `${Math.max(1, Math.round(o / 1024))} Ko`);

function FichiersCloture({ fichiers, onChange }) {
  const ajouter = (fichier) => {
    if (!fichier || fichiers.length >= FICHIERS_MAX) return;
    onChange([...fichiers, fichier]);
  };
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
          <Paperclip className="w-3 h-3" />
          Fichiers joints (optionnel)
        </label>
        <span className={`text-[11px] font-semibold tabular-nums ${fichiers.length >= FICHIERS_MAX ? "text-[#5C1A30]" : "text-gray-400"}`}>
          {fichiers.length}/{FICHIERS_MAX}
        </span>
      </div>
      {fichiers.length > 0 && (
        <ul className="mt-1 space-y-1">
          {fichiers.map((fichier, i) => (
            <li key={`${fichier.name}-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-700">
              <span className="flex items-center gap-1.5 min-w-0">
                <FileText className="w-3.5 h-3.5 flex-shrink-0 text-[#5C1A30]/70" />
                <span className="truncate">{fichier.name}</span>
                <span className="text-gray-400 flex-shrink-0">· {tailleLisible(fichier.size)}</span>
              </span>
              <button
                type="button"
                onClick={() => onChange(fichiers.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                title="Retirer le fichier"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {fichiers.length < FICHIERS_MAX ? (
        <label className="mt-1.5 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-[#5C1A30]/30 text-xs font-medium text-[#5C1A30] hover:bg-[#5C1A30]/5 cursor-pointer">
          <Plus className="w-3.5 h-3.5" />
          {fichiers.length ? "Ajouter un autre fichier" : "Ajouter un fichier"}
          <input
            type="file"
            accept={EXTENSIONS_FICHIERS}
            className="hidden"
            onChange={(e) => {
              ajouter(e.target.files?.[0] || null);
              e.target.value = "";
            }}
          />
        </label>
      ) : (
        <p className="mt-1 text-[11px] text-gray-400">Maximum atteint : retirez un fichier pour en ajouter un autre.</p>
      )}
      <p className="mt-1 text-[10px] text-gray-400">Les fichiers sont joints au mail envoyé aux personnes notifiées.</p>
    </div>
  );
}

function initialFormForStage(stage) {
  if (stage === 1) {
    return {
      qualiteOrganisation: "",
      tempsTraitement: "",
      commentaire: "",
      notifiedUserIds: [],
    };
  }
  if (stage === 2) {
    return {
      // Volet "Traitement comptable" — coché par défaut.
      estTraitementComptable: true,
      nbrFactureAchat: "",
      nbrFactureVente: "",
      nbrDocumentsNonTraites: "",
      tcEbics: "",
      // Volet "RB" (Rapprochement Bancaire) — décoché par défaut, cumulable
      // avec le volet ci-dessus.
      estRB: false,
      rbNumeroCompte: "",
      rbMoisTraites: "",
      rbSoldeRB: "",
      rbSolde512: "",
      rbEcart: "",
      rbEbics: "",
      rbImageDebit: null,
      rbImageCredit: null,
      fichiers: [],
      commentaire: "",
      notifiedUserIds: [],
    };
  }
  return {
    resultatControle: "Conforme",
    tempsPasse: "",
    // Fichiers joints (5 au plus, upload direct vers Vercel Blob, voir
    // handleSubmit) — File tant qu'ils ne sont pas envoyés.
    fichiers: [],
    commentaire: "",
    notifiedUserIds: [],
  };
}

// Au moins une personne doit être prévenue de la clôture — automatiquement
// via l'assigné déjà en place sur l'étape suivante (hasAutoRecipient), sinon
// il faut en choisir au moins une manuellement dans "Personne à notifier".
// Étape 3 (Révision) n'a pas d'étape suivante (hasAutoRecipient toujours
// faux ici) : au moins une personne doit donc toujours être choisie
// manuellement, même principe que les 2 autres étapes.
function isFormValid(stage, formData, hasAutoRecipient) {
  if (stage === 1) {
    return (
      !!formData.qualiteOrganisation &&
      formData.commentaire.trim().length > 0 &&
      (hasAutoRecipient || formData.notifiedUserIds.length > 0)
    );
  }
  if (stage === 2) {
    return (
      formData.commentaire.trim().length > 0 &&
      (hasAutoRecipient || formData.notifiedUserIds.length > 0)
    );
  }
  return hasAutoRecipient || formData.notifiedUserIds.length > 0;
}

// Personnes qui ne reçoivent plus le mail « Étape clôturée » : retirées de
// « Personne à notifier » (Anthony HOARAU, à sa demande du 21/10/2026, le
// suivi étant en place). Même liste côté serveur (clotureEmailController.js).
const EXCLUS_MAIL_CLOTURE = ["anthony.hoarau@alfacomptabilite.com"];
const notifiable = (u) => !EXCLUS_MAIL_CLOTURE.includes(String(u?.email || "").trim().toLowerCase());

function ClotureStageModal({
  ticket,
  etape,
  users: tousUsers,
  utilisateur,
  onClose,
  onSubmitted,
  onBouclerCompta,
}) {
  const users = tousUsers.filter(notifiable);
  const { fetchSuiviCompta } = useEtatGlobal();
  const stage = etape.niveau;
  const meta = STAGE_META[stage];
  const [formData, setFormData] = useState(initialFormForStage(stage));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const etapeSuivante = ticket.etapes.find((e) => e.niveau === stage + 1);
  const hasAutoRecipient = !!etapeSuivante?.assignee?.email;
  const valid = isFormValid(stage, formData, hasAutoRecipient);

  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // multipart systématiquement (même sans fichier) : seul le volet RB de
  // l'étape 2 envoie des images, mais un seul type de requête pour les 3
  // étapes simplifie le contrôleur (req.files est simplement vide sinon).
  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "rbImageDebit" || key === "rbImageCredit") return;
      // Fichiers joints : envoyés à part dans handleSubmit (direct navigateur
      // → Blob, voir uploadFileToBlob) — seules leurs URL sont ajoutées ici.
      if (key === "fichiers") return;
      if (key === "notifiedUserIds") {
        (value || []).forEach((id) => fd.append("notifiedUserIds", id));
        return;
      }
      if (value === null || value === undefined) return;
      fd.append(key, value);
    });
    // Images RB : déposées directement sur Blob dans handleSubmit (seules
    // leurs URL partent avec le formulaire, voir rbImage*Source).
    return fd;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const fd = buildFormData();

      // Étapes 2 et 3 : fichiers joints envoyés un par un, directement du
      // navigateur vers Vercel Blob (contourne la limite 4,5 Mo des Vercel
      // Functions, pas de compression) — même pattern que CreateTodo.js.
      // Étape 2 / RB : les images débit et crédit prennent le même chemin
      // (22/10/2026 : envoyées avec le formulaire, elles dépassaient vite
      // 4,5 Mo à elles deux) ; le serveur les réduit ensuite comme avant.
      const fichiersEnvoyes = [];
      const aEnvoyer = (stage === 2 || stage === 3) ? formData.fichiers || [] : [];
      const imagesRB =
        stage === 2 && formData.estRB
          ? [
              ["rbImageDebitSource", formData.rbImageDebit],
              ["rbImageCreditSource", formData.rbImageCredit],
            ].filter(([, image]) => image)
          : [];
      const total = aEnvoyer.length + imagesRB.length;
      const deposer = (fichier, n) => {
        setUploadProgress({ n, total, pct: 0 });
        return uploadFileToBlob(
          fichier,
          "https://backend-myalfa.vercel.app/api/traitements-comptables/upload-token",
          ({ percentage }) => setUploadProgress({ n, total, pct: Math.round(percentage) }),
        );
      };
      for (let i = 0; i < aEnvoyer.length; i++) {
        const fichier = aEnvoyer[i];
        const url = await deposer(fichier, i + 1);
        fichiersEnvoyes.push({ url, nom: fichier.name, taille: fichier.size });
      }
      for (let i = 0; i < imagesRB.length; i++) {
        const [champ, image] = imagesRB[i];
        fd.append(champ, await deposer(image, aEnvoyer.length + i + 1));
      }
      setUploadProgress(null);
      if (fichiersEnvoyes.length) fd.append("fichiers", JSON.stringify(fichiersEnvoyes));

      const { data: etapeMiseAJour } = await httpClient.post(
        `https://backend-myalfa.vercel.app/api/traitements-comptables/tickets/${ticket.id}/etapes/${stage}/cloture`,
        fd,
      );
      onSubmitted();

      // Handoff : prévenir l'assigné de l'étape suivante s'il existe déjà,
      // ET toute personne choisie manuellement dans "Personne à notifier"
      // (les deux, pas l'un ou l'autre — sinon quelqu'un qui s'ajoute lui-même
      // alors que l'étape suivante a déjà un assigné ne reçoit jamais le mail).
      const manualRecipients = users
        .filter((u) => (formData.notifiedUserIds || []).includes(u.id))
        .map((u) => u.email)
        .filter(Boolean);
      const recipients = Array.from(
        new Set([
          ...(hasAutoRecipient ? [etapeSuivante.assignee.email] : []),
          ...manualRecipients,
        ]),
      );

      // Champs saisis sur le formulaire de clôture (hors commentaire, envoyé à
      // part, et notifiedUserIds, non pertinent pour le destinataire) — pour
      // que le mail porte toute l'info du formulaire, sans avoir à se
      // reconnecter à MyAlfa pour la consulter.
      let champsFormulaire = {};
      if (stage === 1) {
        champsFormulaire = {
          "Qualité d'organisation": formData.qualiteOrganisation,
          "Temps de traitement approximatif": formData.tempsTraitement,
        };
      } else if (stage === 2) {
        champsFormulaire = {
          ...(formData.estTraitementComptable
            ? {
                "Nombre de factures d'achats traitées":
                  formData.nbrFactureAchat,
                "Nombre de factures de ventes traitées":
                  formData.nbrFactureVente,
                "Nombre de documents non traités":
                  formData.nbrDocumentsNonTraites,
                Ebics: formData.tcEbics,
              }
            : {}),
          ...(formData.estRB
            ? {
                "Numéro de compte": formData.rbNumeroCompte,
                "Mois traités": formData.rbMoisTraites,
                "Solde RB": formData.rbSoldeRB,
                "Solde 512": formData.rbSolde512,
                Écart: formData.rbEcart,
                "Ebics (RB)": formData.rbEbics,
              }
            : {}),
        };
      } else if (stage === 3) {
        champsFormulaire = {
          "Résultat du contrôle": formData.resultatControle,
          "Temps passé (h)": formData.tempsPasse,
        };
      }

      if (recipients.length > 0) {
        httpClient
          .post(
            "https://backend-myalfa.vercel.app/api/email-traitement-comptable-cloture",
            {
              clientNom: ticket.client?.nom,
              niveau: stage,
              commentaire: formData.commentaire,
              recipients,
              closedByName: utilisateur
                ? `${utilisateur.name || ""} ${utilisateur.lastName || ""}`.trim()
                : "",
              nextAssigneeName: hasAutoRecipient
                ? `${etapeSuivante.assignee.name || ""} ${etapeSuivante.assignee.lastName || ""}`.trim()
                : "",
              champsFormulaire,
              // Étape 2 uniquement : préciser Traitement comptable / RB / les
              // deux, et joindre les images RB (URL renvoyées par la clôture,
              // uploadées côté serveur) si elles ont été fournies.
              ...(stage === 2
                ? {
                    estTraitementComptable: formData.estTraitementComptable,
                    estRB: formData.estRB,
                    rbImageDebitUrl: etapeMiseAJour?.rbImageDebitUrl || "",
                    rbImageCreditUrl: etapeMiseAJour?.rbImageCreditUrl || "",
                  }
                : {}),
              // Étapes 2 et 3 : fichiers joints, en pièces jointes du mail
              // (et listés avec leur lien).
              ...((stage === 2 || stage === 3) && fichiersEnvoyes.length
                ? { fichiers: Array.isArray(etapeMiseAJour?.fichiers) && etapeMiseAJour.fichiers.length ? etapeMiseAJour.fichiers : fichiersEnvoyes }
                : {}),
            },
          )
          .catch((err) => {
            console.error("Erreur envoi email clôture :", err);
            showStatusToast({
              status: "error",
              titre: "Mail de clôture non envoyé",
              message: "L'étape est bien clôturée, mais le mail aux personnes notifiées n'a pas pu partir.",
            });
          });
      }

      onClose();

      // Liaison Suivi Compta : le backend vient de valider automatiquement la
      // mission correspondante (voir comptaAutoLink.js). Toast de
      // confirmation + refetch silencieux (pas de skeleton — voir
      // fetchSuiviCompta dans EtatGlobal.js) pour que Suivi Compta soit à
      // jour sans que l'utilisateur ait à changer d'écran.
      const missionCompta = STAGE_TO_COMPTA_MISSION[stage];
      const comptaToast = showStatusToast({
        status: "loading",
        titre: "Étape clôturée",
        message: `Mise à jour de Suivi Compta ("${missionCompta}")…`,
      });
      await fetchSuiviCompta();
      comptaToast.update({
        status: "success",
        titre: "Étape clôturée",
        message: `Mission "${missionCompta}" validée automatiquement dans Suivi Compta.`,
      });

      // Étape 3 (Révision) clôturée : le bouclage mensuel Suivi Compta n'est
      // jamais auto-validé (contrairement aux 3 premières missions) — il
      // nécessite une vraie saisie (TVA/IS/TS/CFE, volume de pièces...), donc
      // on demande confirmation avant d'ouvrir son overlay.
      if (stage === 3 && onBouclerCompta) {
        // variant "default" (icône info, pas la poubelle rouge de "danger") —
        // c'est une simple proposition, pas une action destructrice.
        const veutBoucler = await dialog.confirm(
          <>
            L'étape Révision est clôturée. Le formulaire Suivi Comptabilité va
            s'afficher pour boucler ce mois si vous le souhaitez.
            <br />
            <br />
            <span className="text-xs text-slate-400">
              C'est optionnel — vous pouvez ne pas boucler le Suivi Compta
              maintenant et le faire plus tard si besoin.
            </span>
          </>,
          {
            variant: "default",
            title: "Étape Révision clôturée",
            confirmLabel: "Boucler maintenant",
            cancelLabel: "Plus tard",
          },
        );
        if (veutBoucler) onBouclerCompta();
      }
    } catch (err) {
      console.error("Erreur clôture étape :", err);
      setError(err?.response?.data?.error || "Une erreur est survenue lors de la clôture.");
    } finally {
      setUploadProgress(null);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#5C1A30] flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-tight">
                Clôturer le ticket
              </h2>
              <p className="text-xs text-gray-400">
                {ticket.client?.nom} · Niveau {stage} — {meta.label}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-3 bg-[#fff6ee] rounded-xl px-4 py-3">
            <AvatarInitials
              id={etape.assignee?.id}
              name={etape.assignee?.name}
              lastName={etape.assignee?.lastName}
              variant="solid"
              size="w-9 h-9"
              textSize="text-xs"
            />
            <div>
              <p className="text-xs font-semibold text-[#5C1A30] uppercase tracking-wide">
                Intervenant
              </p>
              <p className="text-sm font-bold text-gray-900">
                {etape.assignee?.name} {etape.assignee?.lastName}
              </p>
              {etape.assignee?.poste && (
                <p className="text-xs text-gray-400">{etape.assignee.poste}</p>
              )}
            </div>
          </div>

          <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
            {stage === 2 ? (
              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.estTraitementComptable}
                    onChange={(e) =>
                      handleField("estTraitementComptable", e.target.checked)
                    }
                    className="w-4 h-4 accent-[#5C1A30]"
                  />
                  <span className="text-sm font-bold text-[#5C1A30]">
                    {meta.title}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm font-bold text-[#5C1A30]">RB</span>
                  <input
                    type="checkbox"
                    checked={formData.estRB}
                    onChange={(e) => handleField("estRB", e.target.checked)}
                    className="w-4 h-4 accent-[#5C1A30]"
                  />
                </label>
              </div>
            ) : (
              <p className="text-sm font-bold text-[#5C1A30]">{meta.title}</p>
            )}

            {stage === 1 && (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Qualité d'organisation{" "}
                    <span className="text-red-400 italic font-normal">*</span>
                  </label>
                  <select
                    value={formData.qualiteOrganisation}
                    onChange={(e) =>
                      handleField("qualiteOrganisation", e.target.value)
                    }
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                  >
                    <option value="">Veuillez sélectionner</option>
                    {QUALITE_ORGANISATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Temps de traitement approximatif
                  </label>
                  <input
                    type="text"
                    value={formData.tempsTraitement}
                    onChange={(e) =>
                      handleField("tempsTraitement", e.target.value)
                    }
                    placeholder="ex : 1h30"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Commentaire{" "}
                    <span className="text-red-400 italic font-normal">*</span>
                  </label>
                  <textarea
                    value={formData.commentaire}
                    onChange={(e) => handleField("commentaire", e.target.value)}
                    rows={3}
                    placeholder="Observations sur le tri et la numérisation..."
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-none"
                  />
                </div>
                <UserSearchTagPicker
                  allUsers={users}
                  label="Personne à notifier :"
                  required={!hasAutoRecipient}
                  selectedIds={formData.notifiedUserIds}
                  onAdd={(user) =>
                    handleField("notifiedUserIds", [
                      ...formData.notifiedUserIds,
                      user.id,
                    ])
                  }
                  onRemove={(id) =>
                    handleField(
                      "notifiedUserIds",
                      formData.notifiedUserIds.filter((i) => i !== id),
                    )
                  }
                />
                {hasAutoRecipient ? (
                  <p className="text-[11px] text-gray-400 -mt-1">
                    {etapeSuivante.assignee.name}{" "}
                    {etapeSuivante.assignee.lastName} sera de toute façon
                    prévenu(e) automatiquement (déjà affecté(e) à l'étape
                    suivante).
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-400 -mt-1">
                    Aucun collaborateur affecté sur l'étape suivante — au moins
                    une personne doit être choisie ici.
                  </p>
                )}
              </>
            )}

            {stage === 2 && (
              <>
                {formData.estTraitementComptable && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Nombre de facture d'achats traitées
                        </label>
                        <input
                          type="number"
                          value={formData.nbrFactureAchat}
                          onChange={(e) =>
                            handleField("nbrFactureAchat", e.target.value)
                          }
                          placeholder="ex : 24"
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Nombre de facture de ventes traitées
                        </label>
                        <input
                          type="number"
                          value={formData.nbrFactureVente}
                          onChange={(e) =>
                            handleField("nbrFactureVente", e.target.value)
                          }
                          placeholder="ex : 18"
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Nombre de documents non traités
                      </label>
                      <input
                        type="number"
                        value={formData.nbrDocumentsNonTraites}
                        onChange={(e) =>
                          handleField("nbrDocumentsNonTraites", e.target.value)
                        }
                        placeholder="ex : 3"
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Ebics
                      </label>
                      <select
                        value={formData.tcEbics}
                        onChange={(e) => handleField("tcEbics", e.target.value)}
                        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                      >
                        <option value="">—</option>
                        <option value="oui">Oui</option>
                        <option value="non">Non</option>
                      </select>
                    </div>
                  </>
                )}

                {formData.estRB && (
                  <div className="border border-gray-100 rounded-xl p-3 space-y-3 bg-gray-50/50">
                    <p className="text-xs font-bold text-[#5C1A30] uppercase tracking-wide">
                      RB
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Numéro compte
                        </label>
                        <input
                          type="text"
                          value={formData.rbNumeroCompte}
                          onChange={(e) =>
                            handleField("rbNumeroCompte", e.target.value)
                          }
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Mois traités
                        </label>
                        <input
                          type="text"
                          value={formData.rbMoisTraites}
                          onChange={(e) =>
                            handleField("rbMoisTraites", e.target.value)
                          }
                          placeholder="ex : Janvier 2026"
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Solde RB
                        </label>
                        <input
                          type="text"
                          value={formData.rbSoldeRB}
                          onChange={(e) =>
                            handleField("rbSoldeRB", e.target.value)
                          }
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Solde 512
                        </label>
                        <input
                          type="text"
                          value={formData.rbSolde512}
                          onChange={(e) =>
                            handleField("rbSolde512", e.target.value)
                          }
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Écart
                        </label>
                        <input
                          type="text"
                          value={formData.rbEcart}
                          onChange={(e) =>
                            handleField("rbEcart", e.target.value)
                          }
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Ebics
                        </label>
                        <select
                          value={formData.rbEbics}
                          onChange={(e) =>
                            handleField("rbEbics", e.target.value)
                          }
                          className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                        >
                          <option value="">—</option>
                          <option value="oui">Oui</option>
                          <option value="non">Non</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Image débit
                        </label>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp,.heic"
                          onChange={(e) =>
                            handleField(
                              "rbImageDebit",
                              e.target.files?.[0] || null,
                            )
                          }
                          className="w-full mt-1 text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#5C1A30]/10 file:text-[#5C1A30] hover:file:bg-[#5C1A30]/20 cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">
                          Image crédit
                        </label>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.webp,.heic"
                          onChange={(e) =>
                            handleField(
                              "rbImageCredit",
                              e.target.files?.[0] || null,
                            )
                          }
                          className="w-full mt-1 text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#5C1A30]/10 file:text-[#5C1A30] hover:file:bg-[#5C1A30]/20 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <FichiersCloture fichiers={formData.fichiers} onChange={(l) => handleField("fichiers", l)} />
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Commentaire{" "}
                    <span className="text-red-400 italic font-normal">*</span>
                  </label>
                  <textarea
                    value={formData.commentaire}
                    onChange={(e) => handleField("commentaire", e.target.value)}
                    rows={3}
                    placeholder="Observations, documents en attente, points transmis à la révision..."
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-none"
                  />
                </div>
                <UserSearchTagPicker
                  allUsers={users}
                  label="Personne à notifier :"
                  required={!hasAutoRecipient}
                  selectedIds={formData.notifiedUserIds}
                  onAdd={(user) =>
                    handleField("notifiedUserIds", [
                      ...formData.notifiedUserIds,
                      user.id,
                    ])
                  }
                  onRemove={(id) =>
                    handleField(
                      "notifiedUserIds",
                      formData.notifiedUserIds.filter((i) => i !== id),
                    )
                  }
                />
                {hasAutoRecipient ? (
                  <p className="text-[11px] text-gray-400 -mt-1">
                    {etapeSuivante.assignee.name}{" "}
                    {etapeSuivante.assignee.lastName} sera de toute façon
                    prévenu(e) automatiquement (déjà affecté(e) à l'étape
                    suivante).
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-400 -mt-1">
                    Aucun collaborateur affecté sur l'étape suivante — au moins
                    une personne doit être choisie ici.
                  </p>
                )}
              </>
            )}

            {stage === 3 && (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1.5">
                    Résultat du contrôle
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {["Conforme", "Réserves"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleField("resultatControle", opt)}
                        className={`px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                          formData.resultatControle === opt
                            ? "border-[#5C1A30] bg-[#5C1A30]/5 text-[#5C1A30]"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Temps passé (h)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.tempsPasse}
                    onChange={(e) => handleField("tempsPasse", e.target.value)}
                    placeholder="ex : 1,5"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">
                    Commentaire de clôture
                  </label>
                  <textarea
                    value={formData.commentaire}
                    onChange={(e) => handleField("commentaire", e.target.value)}
                    rows={3}
                    placeholder="Observations, points de vigilance transmis au niveau suivant..."
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040] resize-none"
                  />
                </div>
                <FichiersCloture fichiers={formData.fichiers} onChange={(l) => handleField("fichiers", l)} />
                <UserSearchTagPicker
                  allUsers={users}
                  label="Personne à notifier :"
                  required={!hasAutoRecipient}
                  selectedIds={formData.notifiedUserIds}
                  onAdd={(user) =>
                    handleField("notifiedUserIds", [
                      ...formData.notifiedUserIds,
                      user.id,
                    ])
                  }
                  onRemove={(id) =>
                    handleField(
                      "notifiedUserIds",
                      formData.notifiedUserIds.filter((i) => i !== id),
                    )
                  }
                />
                <p className="text-[11px] text-gray-400 -mt-1">
                  Dernière étape du dossier — au moins une personne doit être
                  choisie pour être informée de la clôture.
                </p>
              </>
            )}
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[#840040] text-[#840040] text-sm font-semibold hover:bg-[#840040]/5 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!valid || submitting}
            onClick={handleSubmit}
            className={`px-5 py-2 rounded-xl text-sm font-semibold transition-colors ${
              !valid || submitting
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : stage === 3
                  ? "bg-[#5C1A30] text-white hover:bg-[#4a1526]"
                  : "bg-white border border-[#5C1A30] text-[#5C1A30] hover:bg-[#5C1A30]/5"
            }`}
          >
            {submitting
              ? uploadProgress !== null
                ? `Envoi du fichier ${uploadProgress.n}/${uploadProgress.total}... ${uploadProgress.pct}%`
                : "Enregistrement..."
              : meta.submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClotureStageModal;
